import websocketService from '../websocket';
import { createWebSocketRequest } from './utils';

/**
 * Subscribe to user data updates
 * @returns {Promise<Object>} Promise resolving to the subscription status
 */
export const subscribeUser = () => {
  return createWebSocketRequest({
    eventName: 'subscribe-user-ack',
    requestType: 'subscribe-user',
    requestData: {},
    dataValidator: (data) => {
      // Validate the expected response format
      if (!data || !data.status) {
        throw new Error('Invalid response format');
      }
      
      if (data.status === 'failed') {
        throw new Error(data.info || 'Error subscribing user');
      }
      
      return data;
    },
    errorMessage: 'Failed to subscribe to user data',
    timeout: 10000,
    maxRetries: 2
  });
};

/**
 * Unsubscribe from user data updates
 * @returns {Promise<Object>} Promise resolving to the unsubscription status
 */
export const unsubscribeUser = () => {
  return createWebSocketRequest({
    eventName: 'un-subscribe-user-ack',
    requestType: 'unsubscribe-user',
    requestData: {},
    dataValidator: (data) => {
      // Validate the expected response format
      if (!data || !data.status) {
        throw new Error('Invalid response format');
      }
      
      if (data.status === 'failed') {
        throw new Error(data.info || 'User not subscribed');
      }
      
      return data;
    },
    errorMessage: 'Failed to unsubscribe from user data',
    timeout: 10000,
    maxRetries: 2
  });
};

/**
 * Get current user subscription status
 * @returns {Promise<Object>} Promise resolving to the current subscription status
 */
export const getSubscriptionStatus = () => {
  return createWebSocketRequest({
    eventName: 'get-subscription-status-response',
    requestType: 'get-subscription-status',
    requestData: {},
    dataValidator: (data) => {
      // Validate the expected response format
      if (!data || !data.status) {
        throw new Error('Invalid response format');
      }
      
      // Return the data with isSubscribed flag for easier consumption
      return {
        ...data,
        isSubscribed: data.info === 'User subscribed'
      };
    },
    errorMessage: 'Failed to get subscription status',
    timeout: 10000,
    maxRetries: 2
  });
};

// Cache for user data
let userDataCache = null;

/**
 * Get the latest user data from the cache
 * @returns {Object|null} The cached user data or null if no data is available
 */
export const getUserData = () => {
  return userDataCache;
};

// Set up event listener for userInfo messages
websocketService.on('message', (message) => {
  // Check if message is userInfo type
  if (message && message.type === 'userInfo' && message.data) {
    // Update the cache with the user data
    userDataCache = message.data;
    
    // Emit an event for components to listen to
    websocketService.emit('user-cache-updated', userDataCache);
  }
});

// Hook for components to use
export const useUserSubscription = (callback) => {
  websocketService.on('user-cache-updated', callback);
  
  // Return cleanup function
  return () => {
    websocketService.off('user-cache-updated', callback);
  };
}; 